import { useCallback, useEffect, useRef, useState } from 'react';
import useAnalytics from '@/hooks/useAnalytics';
import useGuestAddress, {
  getGuestAddress,
  isCountyPreview,
  type CountyPreviewSource,
} from '@/hooks/useGuestAddress';
import { locateDeviceCounty } from '@/hooks/useCountyPreview';
import {
  queryGeolocationPermission,
  watchGeolocationPermission,
  type GeolocationPermission,
} from '@/utils/deviceLocation';

export type ExactLocationOrigin = 'ballot_tip' | 'ballot_notice';

/**
 * How the last "Use my exact location" attempt ended, when it didn't replace
 * the preview with the voter's street address.
 * ``denied``: the browser refused, and won't ask again.
 * ``failed``: no position or no lookup; worth another try.
 * ``approximate``: the device shared only an approximate location.
 */
export type ExactLocationOutcome = 'denied' | 'failed' | 'approximate';

export interface ExactLocationState {
  /** Show the "Use my exact location" action. */
  offered: boolean;
  locating: boolean;
  outcome: ExactLocationOutcome | null;
  /** ``keyboard``: the control had keyboard focus, so focus follows the result. */
  locate: (from: ExactLocationOrigin, keyboard?: boolean) => void;
}

interface UseExactLocationOptions {
  /** After a precise fix ends the preview, which unmounts the tip and notice. */
  onExactAddress?: (keyboard: boolean) => void;
}

/**
 * One "Use my exact location" state for a county-preview ballot, shared by the
 * tip above it and the notice below it so both offer and report the same thing.
 * Offered on an IP guess while the browser might still grant location: a
 * device fix is already the best that device will share.
 */
const useExactLocation = (
  source: CountyPreviewSource | null,
  { onExactAddress }: UseExactLocationOptions = {}
): ExactLocationState => {
  const { track } = useAnalytics();
  const { setAddress } = useGuestAddress();
  const [permission, setPermission] = useState<GeolocationPermission | null>(null);
  const [locating, setLocating] = useState(false);
  const [outcome, setOutcome] = useState<ExactLocationOutcome | null>(null);
  const locatingRef = useRef(false);
  const mountedRef = useRef(true);
  const onExactAddressRef = useRef(onExactAddress);
  useEffect(() => {
    onExactAddressRef.current = onExactAddress;
  });
  const active = source !== null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!active) {
      // A later preview starts fresh.
      setOutcome(null);
      return;
    }
    let cancelled = false;
    void queryGeolocationPermission().then((state) => {
      if (!cancelled) setPermission(state);
    });
    const stop = watchGeolocationPermission((state) => {
      setPermission(state);
      // Unblocked in site settings: offer the action again.
      if (state !== 'denied') setOutcome((last) => (last === 'denied' ? null : last));
    });
    return () => {
      cancelled = true;
      stop();
    };
  }, [active]);

  const locate = useCallback(
    async (from: ExactLocationOrigin, keyboard: boolean) => {
      if (locatingRef.current) return;
      locatingRef.current = true;
      const grant = permission === 'granted' ? 'prior' : 'granted_now';
      track('Landing Location Requested', { permission_state: permission ?? 'prompt', from });
      setLocating(true);
      setOutcome(null);
      const result = await locateDeviceCounty();
      locatingRef.current = false;
      if (!mountedRef.current) return;
      setLocating(false);
      if (result.exact) {
        // The voter may have entered their own address meanwhile; keep theirs.
        if (!isCountyPreview(getGuestAddress())) return;
        setAddress({
          formatted_address: result.exact.formatted_address,
          address_components: result.exact.address_components,
        });
        track('Landing Exact Address Found', { permission: grant, from });
        onExactAddressRef.current?.(keyboard);
        return;
      }
      if (!result.preview) {
        const denied = result.failure === 'denied';
        setOutcome(denied ? 'denied' : 'failed');
        if (denied) setPermission('denied');
        track('Landing County Preview Failed', { reason: result.failure, source: 'device', from });
        return;
      }
      setPermission('granted');
      if (!isCountyPreview(getGuestAddress())) return;
      const { preview } = result;
      setOutcome('approximate');
      setAddress({
        formatted_address: preview.formatted_address,
        address_components: preview.address_components,
        county_preview: {
          jurisdiction: preview.jurisdiction,
          state: preview.state,
          source: 'device',
        },
      });
      track('Landing County Preview Shown', {
        state: preview.state,
        permission: grant,
        source: 'device',
        from,
      });
    },
    [permission, track, setAddress]
  );

  const offered =
    source === 'ip' &&
    (permission === 'prompt' || permission === 'granted') &&
    outcome !== 'denied';

  return {
    offered,
    locating,
    outcome,
    locate: (from, keyboard = false) => void locate(from, keyboard),
  };
};

export default useExactLocation;
